import React, { useEffect, useRef, useState } from "react";
import {
    Alert,
    Button,
    Card,
    Empty,
    List,
    Input,
    Space,
    Spin,
    Tag,
    Typography,
} from "antd";
import WorkbenchService from "../../workbench/services/WorkbenchService";
import { WbFact, WbFactValue } from "../../workbench/types/WbTypes";
import { formatDateTime } from "../utils/workbenchNewSubjectView";
import WorkbenchBackendAssociations from "./WorkbenchBackendAssociations";
import WorkbenchFactCorrection from "./WorkbenchFactCorrection";

const service = new WorkbenchService();

const WorkbenchNewFactsTab: React.FC<{
    subjectId: string;
    onReview?: () => void;
}> = ({ subjectId, onReview }) => {
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [attentionOnly, setAttentionOnly] = useState(false);
    const [revision, setRevision] = useState(0);
    const [facts, setFacts] = useState<WbFact[]>([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [reviewFailed, setReviewFailed] = useState(false);
    const [reasons, setReasons] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const generation = useRef(0);
    useEffect(() => {
        let current = true;
        generation.current++;
        setSelectedField(null);
        setAttentionOnly(false);
        setSaving(false);
        setReasons({});
        setReviewFailed(false);
        setLoading(true);
        setFailed(false);
        setFacts([]);
        service
            .fetchSubjectFacts(subjectId)
            .then((result) => {
                if (!current) return;
                if (result.isOk()) setFacts(result.value);
                else setFailed(true);
            })
            .catch(() => {
                if (current) setFailed(true);
            })
            .finally(() => {
                if (current) setLoading(false);
            });
        return () => {
            current = false;
            generation.current++;
        };
    }, [subjectId, revision]);
    const review = async (
        field: string,
        action: "Confirm" | "Correct" | "Clear" | "Reject" | "Restore",
        value?: string,
        evidenceId?: string,
        details?: WbFactValue,
        reason?: string,
    ) => {
        const current = generation.current;
        setSaving(true);
        setReviewFailed(false);
        try {
            const result = await service.reviewSubjectFact(
                subjectId,
                field,
                action,
                value,
                evidenceId,
                details,
                reason,
            );
            if (current !== generation.current) return;
            if (result.isOk()) {
                setFacts(result.value);
                onReview?.();
            } else setReviewFailed(true);
        } catch {
            if (current === generation.current) setReviewFailed(true);
        } finally {
            if (current === generation.current) setSaving(false);
        }
    };
    const needsAttention = (fact: WbFact) =>
        fact.state === "NeedsReview" ||
        fact.identityConflict ||
        fact.hasDisagreement ||
        fact.state === "Partial";
    const attentionCount = facts.filter(needsAttention).length;
    const visibleFacts = attentionOnly ? facts.filter(needsAttention) : facts;
    const selected =
        visibleFacts.find((fact) => fact.field === selectedField) ??
        visibleFacts.find(needsAttention) ??
        visibleFacts[0];
    if (loading) return <Spin aria-label="Loading facts" />;
    if (failed)
        return <Alert type="error" message="Facts could not be loaded" />;
    if (!facts.length) return <Empty description="No facts available" />;
    return (
        <div className="workbench-facts">
            {reviewFailed && (
                <Alert
                    type="error"
                    message="Review could not be saved. Refresh facts before retrying."
                />
            )}
            <div className="workbench-facts-toolbar">
                <div>
                    <h3>Subject facts</h3>
                    <p>
                        Select a fact to compare sources and review its value.
                    </p>
                </div>
                <Space size={6}>
                    <Button
                        size="small"
                        type={!attentionOnly ? "primary" : "default"}
                        onClick={() => setAttentionOnly(false)}
                    >
                        All facts ({facts.length})
                    </Button>
                    <Button
                        size="small"
                        type={attentionOnly ? "primary" : "default"}
                        onClick={() => setAttentionOnly(true)}
                    >
                        Needs attention ({attentionCount})
                    </Button>
                </Space>
            </div>
            <div className="workbench-facts-layout">
                <div
                    className="workbench-facts-overview"
                    aria-label="Subject facts"
                >
                    <div className="workbench-facts-column-labels">
                        <span>Fact</span>
                        <span>Current value</span>
                    </div>
                    {visibleFacts.map((fact) => (
                        <Button
                            key={fact.field}
                            type="text"
                            className={`workbench-facts-row ${selected?.field === fact.field ? "is-selected" : ""}`}
                            aria-label={`Review ${fact.label}`}
                            aria-pressed={selected?.field === fact.field}
                            onClick={() => setSelectedField(fact.field)}
                        >
                            <span className="workbench-facts-label">
                                {fact.label}
                            </span>
                            <span
                                className={`workbench-facts-value ${needsAttention(fact) ? "needs-attention" : ""}`}
                            >
                                {fact.state === "NeedsReview"
                                    ? "Conflicting values"
                                    : fact.value || "Not available"}
                                <small>
                                    {fact.identityConflict
                                        ? "Identity conflict"
                                        : fact.hasDisagreement
                                          ? "Disagreement"
                                          : fact.hasManualOverride
                                            ? "Manual override"
                                            : fact.state === "Partial"
                                              ? "Partial value"
                                              : !fact.value &&
                                                  fact.state !== "NeedsReview"
                                                ? "Missing"
                                                : ""}
                                </small>
                            </span>
                            <span
                                className="workbench-facts-chevron"
                                aria-hidden="true"
                            >
                                ›
                            </span>
                        </Button>
                    ))}
                    {!visibleFacts.length && (
                        <p className="workbench-facts-clear">
                            No facts need attention.
                        </p>
                    )}
                </div>
                <div
                    className="workbench-facts-detail"
                    role="region"
                    aria-label="Selected fact review"
                >
                    {(selected ? [selected] : []).map((fact) => (
                        <Card
                            key={fact.field}
                            title={fact.label}
                            className="workbench-facts-review-card"
                            size="small"
                        >
                            <div className="workbench-facts-current">
                                <span className="workbench-facts-eyebrow">
                                    Current value
                                </span>
                                {fact.state === "NeedsReview" ? (
                                    <Tag color="warning">Needs review</Tag>
                                ) : (
                                    <Typography.Text strong>
                                        {fact.value || "Not available"}
                                    </Typography.Text>
                                )}
                                {fact.hasManualOverride && (
                                    <Tag color="blue">Manual override</Tag>
                                )}
                                {fact.hasDisagreement && (
                                    <Tag color="warning">
                                        Disagreement retained
                                    </Tag>
                                )}
                                {fact.state === "Partial" && (
                                    <Tag>Partial value</Tag>
                                )}
                            </div>
                            {fact.identityConflict && (
                                <Alert
                                    type="warning"
                                    message="Grouping identity needs review"
                                    description="Correct the underlying evidence or Task links. Changing this displayed fact does not change the Subject identity."
                                />
                            )}
                            <details className="workbench-facts-disclosure">
                                <summary>
                                    Correct value
                                    {fact.hasManualOverride
                                        ? " / clear override"
                                        : ""}
                                </summary>
                                <Space wrap>
                                    <WorkbenchFactCorrection
                                        key={`${subjectId}:${fact.field}`}
                                        field={fact.field}
                                        label={fact.label}
                                        disabled={saving}
                                        onSave={(value, details) =>
                                            review(
                                                fact.field,
                                                "Correct",
                                                value,
                                                undefined,
                                                details,
                                            )
                                        }
                                    />
                                    {fact.hasManualOverride && (
                                        <Button
                                            aria-label={`Clear ${fact.label} override`}
                                            disabled={saving}
                                            onClick={() =>
                                                review(fact.field, "Clear")
                                            }
                                        >
                                            Clear override
                                        </Button>
                                    )}
                                </Space>
                            </details>
                            <div className="workbench-facts-section-title">
                                Source evidence{" "}
                                <span>{fact.evidence.length}</span>
                            </div>
                            {!fact.evidence.length ? (
                                <p className="workbench-facts-muted">
                                    No source evidence yet. You can add a
                                    corrected value above.
                                </p>
                            ) : (
                                <List
                                    dataSource={fact.evidence}
                                    size="small"
                                    renderItem={(evidence) => (
                                        <List.Item key={evidence.id}>
                                            <div className="workbench-facts-evidence">
                                                <Typography.Text>
                                                    {evidence.value}
                                                </Typography.Text>
                                                {evidence.details?.partyId && (
                                                    <Typography.Text type="secondary">
                                                        Party ID:{" "}
                                                        {
                                                            evidence.details
                                                                .partyId
                                                        }
                                                    </Typography.Text>
                                                )}
                                                {evidence.details
                                                    ?.partyScope && (
                                                    <Typography.Text type="secondary">
                                                        Reference party:{" "}
                                                        {
                                                            evidence.details
                                                                .partyScope
                                                        }
                                                    </Typography.Text>
                                                )}
                                                <Space wrap>
                                                    <Typography.Text>
                                                        {evidence.sourceName}
                                                    </Typography.Text>
                                                    <Tag>
                                                        {evidence.sourceType ===
                                                        "Unknown"
                                                            ? "Source unknown"
                                                            : evidence.sourceType}
                                                    </Tag>
                                                    {evidence.isRejected && (
                                                        <Tag color="error">
                                                            Rejected
                                                        </Tag>
                                                    )}
                                                    {evidence.isWithdrawn && (
                                                        <Tag>
                                                            Association removed
                                                        </Tag>
                                                    )}
                                                    {!evidence.isCurrent &&
                                                        !evidence.isWithdrawn && (
                                                            <Tag>
                                                                Previous
                                                                observation
                                                            </Tag>
                                                        )}
                                                    {evidence.observedAt && (
                                                        <Typography.Text type="secondary">
                                                            {formatDateTime(
                                                                evidence.observedAt,
                                                            )}
                                                        </Typography.Text>
                                                    )}
                                                    <Typography.Text type="secondary">
                                                        {evidence.confidence ==
                                                        null
                                                            ? "Confidence unknown"
                                                            : `Confidence ${Math.round(evidence.confidence * 100)}%`}
                                                    </Typography.Text>
                                                </Space>
                                                <div className="workbench-facts-evidence-actions">
                                                    <Button
                                                        size="small"
                                                        disabled={saving}
                                                        aria-label={`Confirm ${evidence.value}`}
                                                        onClick={() =>
                                                            review(
                                                                fact.field,
                                                                "Confirm",
                                                                undefined,
                                                                evidence.id,
                                                            )
                                                        }
                                                    >
                                                        Confirm value
                                                    </Button>
                                                    {evidence.isRejected ? (
                                                        <Button
                                                            size="small"
                                                            disabled={saving}
                                                            aria-label={`Restore ${evidence.value}`}
                                                            onClick={() =>
                                                                review(
                                                                    fact.field,
                                                                    "Restore",
                                                                    undefined,
                                                                    evidence.id,
                                                                )
                                                            }
                                                        >
                                                            Restore evidence
                                                        </Button>
                                                    ) : (
                                                        <details className="workbench-facts-reject">
                                                            <summary>
                                                                Reject evidence
                                                            </summary>
                                                            <Space wrap>
                                                                <Input
                                                                    aria-label={`Rejection reason for ${evidence.value}`}
                                                                    placeholder="Reason for rejection"
                                                                    value={
                                                                        reasons[
                                                                            evidence
                                                                                .id
                                                                        ] || ""
                                                                    }
                                                                    disabled={
                                                                        saving
                                                                    }
                                                                    onChange={(
                                                                        event,
                                                                    ) =>
                                                                        setReasons(
                                                                            {
                                                                                ...reasons,
                                                                                [evidence.id]:
                                                                                    event
                                                                                        .target
                                                                                        .value,
                                                                            },
                                                                        )
                                                                    }
                                                                />
                                                                <Button
                                                                    size="small"
                                                                    disabled={
                                                                        saving ||
                                                                        !reasons[
                                                                            evidence
                                                                                .id
                                                                        ]?.trim()
                                                                    }
                                                                    aria-label={`Reject ${evidence.value}`}
                                                                    onClick={() =>
                                                                        review(
                                                                            fact.field,
                                                                            "Reject",
                                                                            undefined,
                                                                            evidence.id,
                                                                            undefined,
                                                                            reasons[
                                                                                evidence
                                                                                    .id
                                                                            ],
                                                                        )
                                                                    }
                                                                >
                                                                    Reject
                                                                    evidence
                                                                </Button>
                                                            </Space>
                                                        </details>
                                                    )}
                                                </div>
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            )}
                            {!!fact.history?.length && (
                                <details className="workbench-facts-disclosure">
                                    <summary>
                                        Review history ({fact.history.length})
                                    </summary>
                                    <List
                                        size="small"
                                        dataSource={fact.history}
                                        renderItem={(entry) => (
                                            <List.Item key={entry.id}>
                                                {entry.action} · {entry.actorId}{" "}
                                                · {formatDateTime(entry.at)} ·{" "}
                                                {entry.previousValue ||
                                                    "Not selected"}{" "}
                                                →{" "}
                                                {entry.value ||
                                                    "Automatic selection"}
                                                {entry.reason &&
                                                    ` · ${entry.reason}`}
                                            </List.Item>
                                        )}
                                    />
                                </details>
                            )}
                        </Card>
                    ))}
                </div>
            </div>
            <WorkbenchBackendAssociations
                key={subjectId}
                subjectId={subjectId}
                onChange={() => {
                    setRevision((x) => x + 1);
                    onReview?.();
                }}
            />
        </div>
    );
};

export default WorkbenchNewFactsTab;
